import { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import useUserStore from "../../commons/store/user/useUser";
import { useConversation } from "@elevenlabs/react";
import i18n from "../../i18n";
import "./ElevenLabsSDKWidget.css";
import { getUserAsQParam } from "../../utils/MeetingsTime/meetingValidations";

const ElevenLabsSDKWidget = () => {
  const { t } = useTranslation("assistant");
  const { user, getAccessTokenSilently } = useAuth0();
  const { userInfo } = useUserStore();
  const [conversationStarted, setConversationStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [micMuted, setMicMuted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const conversation = useConversation({
    micMuted,
    onConnect: () => {
      console.log("✅ Conectado al agente");
      setConversationStarted(true);
    },
    onDisconnect: () => {
      console.log("❌ Desconectado del agente");
      setConversationStarted(false);
    },
    onMessage: (message) => {
      console.log("📨 Mensaje recibido:", message);
      // Agregar mensajes al historial
      if (message.type === "user_transcript" && message.user_transcript) {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: message.user_transcript },
        ]);
      } else if (message.type === "agent_response" && message.agent_response) {
        setMessages((prev) => [
          ...prev,
          { role: "agent", content: message.agent_response },
        ]);
      }
    },
    onError: (error) => {
      console.error("❌ Error:", error);
    },
    clientTools: {
      // show_user_alert: async () => {
      //   try {
      //     const userName =
      //       userInfo?.user_info?.first_name || user?.given_name || "Usuario";
      //     const fullName =
      //       userInfo?.user_info?.complete_name || user?.name || userName;

      //     console.log("🔧 Client Tool show_user_alert ejecutado desde el SDK");

      //     alert(
      //       `¡Hola ${fullName}! 👋 (Desde el SDK)\n\nTus nombres: ${userName}`
      //     );

      //     return `Alert mostrado exitosamente para ${fullName}`;
      //   } catch (error) {
      //     console.error("❌ Error en client tool (SDK):", error);
      //     throw error;
      //   }
      // },
      open_instant_meeting: async () => {
        try {
          console.log(
            "🔧 Client Tool open_instant_meeting ejecutado desde el SDK"
          );

          // Obtener el token de Auth0
          const token = await getAccessTokenSilently();

          if (!token) {
            console.error("❌ No se pudo obtener el token de Auth0");
            return "Error: No se pudo autenticar";
          }

          // Obtener la organización del usuario
          const orgName =
            user?.["https://wherebyfront.vercel.app/orgname"] || "wave4";

          // Hacer la petición para obtener el instant meeting
          const response = await fetch(
            `https://wlshop.devserverwave4.com/whereby/get_instant_meeting_db?org_name=${orgName}${getUserAsQParam(user?.sub)}`,
            {
              method: "GET",
              headers: {
                accept: "application/json",
                authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`Error en la petición: ${response.status}`);
          }

          const instantMeetingData = await response.json();

          // Construir la URL del instant meeting
          const userRoomName = instantMeetingData?.userRoomName;
          const meetingId = instantMeetingData?.meetingId;

          if (!userRoomName || !meetingId) {
            console.error("❌ No se encontró información del instant meeting");
            return "Error: No se encontró información del instant meeting";
          }

          // Construir la URL completa
          const meetingUrl = `${window.location.origin}/${userRoomName}/${meetingId}`;

          console.log(`✅ Meeting URL preparada: ${meetingUrl}`);

          // ✅ Crear un modal interactivo con botón
          // El usuario hace click en el botón = acción directa = NO bloqueado
          const modal = document.createElement("div");
          modal.id = "instant-meeting-modal";
          modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(44, 53, 61, 0.85);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 999999;
            animation: fadeInModal 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          `;

          modal.innerHTML = `
            <style>
              @keyframes fadeInModal {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideUpModal {
                from { transform: translateY(20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
              @keyframes floatModal {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
            </style>
            <div style="
              background: white;
              padding: 40px;
              border-radius: 16px;
              text-align: center;
              max-width: 420px;
              width: 90%;
              box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
              animation: slideUpModal 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            ">
              <div style="
                width: 80px;
                height: 80px;
                margin: 0 auto 24px;
                background: #F2B84B;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 40px;
                box-shadow: 0 4px 12px rgba(242, 184, 75, 0.3);
                animation: floatModal 3s ease-in-out infinite;
              ">
                🎥
              </div>
              <h2 style="
                margin: 0 0 12px 0;
                color: #2C353D;
                font-size: 24px;
                font-weight: 600;
                font-family: 'Poppins', sans-serif;
              ">Your Meeting is Ready!</h2>
              <p style="
                color: #748FB5;
                margin: 0 0 32px 0;
                font-size: 15px;
                line-height: 1.5;
                font-family: 'Nunito', sans-serif;
              ">
                Click the button below to open your instant meeting in a new tab.
              </p>
              <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
                <button id="openMeetingBtn" style="
                  background: #F2B84B;
                  color: #2C353D;
                  border: none;
                  padding: 14px 32px;
                  border-radius: 12px;
                  font-size: 15px;
                  font-weight: 700;
                  cursor: pointer;
                  box-shadow: 0 4px 12px rgba(242, 184, 75, 0.3);
                  transition: all 0.2s;
                  font-family: 'Nunito', sans-serif;
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 16px rgba(242, 184, 75, 0.4)'; this.style.background='#F2C777'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(242, 184, 75, 0.3)'; this.style.background='#F2B84B'">
                  🚀 Open Meeting
                </button>
                <button id="cancelBtn" style="
                  background: transparent;
                  color: #748FB5;
                  border: 2px solid #BAC3CA;
                  padding: 14px 32px;
                  border-radius: 12px;
                  font-size: 15px;
                  font-weight: 600;
                  cursor: pointer;
                  transition: all 0.2s;
                  font-family: 'Nunito', sans-serif;
                " onmouseover="this.style.background='#F7F7F7'; this.style.borderColor='#748FB5'" onmouseout="this.style.background='transparent'; this.style.borderColor='#BAC3CA'">
                  Cancel
                </button>
              </div>
            </div>
          `;

          document.body.appendChild(modal);

          // Event listener para abrir la reunión
          document
            .getElementById("openMeetingBtn")
            .addEventListener("click", () => {
              // ✅ Este click SÍ es una acción directa del usuario
              // El navegador NO bloqueará esta ventana
              window.open(meetingUrl, "_blank", "noopener,noreferrer");
              document.body.removeChild(modal);
            });

          // Event listener para cancelar
          document.getElementById("cancelBtn").addEventListener("click", () => {
            document.body.removeChild(modal);
          });

          // Cerrar modal al hacer click fuera de él
          modal.addEventListener("click", (e) => {
            if (e.target === modal) {
              document.body.removeChild(modal);
            }
          });

          return `Meeting is ready! Please click the button to open it.`;
        } catch (error) {
          console.error("❌ Error en client tool open_instant_meeting:", error);
          return `Error al abrir instant meeting: ${error.message}`;
        }
      },
      change_language: async ({ language }) => {
        try {
          console.log(
            `🔧 Client Tool change_language ejecutado desde el SDK - Idioma: ${language}`
          );

          // Validar el idioma recibido
          const validLanguages = ["en", "es", "fil"];
          const languageMap = {
            english: "en",
            inglés: "en",
            ingles: "en",
            spanish: "es",
            español: "es",
            espanol: "es",
            filipino: "fil",
            tagalog: "fil",
          };

          // Normalizar el idioma
          const normalizedLanguage = language.toLowerCase().trim();
          const languageCode =
            languageMap[normalizedLanguage] || normalizedLanguage;

          // Verificar si el idioma es válido
          if (!validLanguages.includes(languageCode)) {
            console.error(`❌ Idioma no válido: ${language}`);
            return `Error: El idioma "${language}" no es válido. Los idiomas disponibles son: Inglés (English), Español (Spanish) y Filipino.`;
          }

          // Cambiar el idioma usando i18n
          await i18n.changeLanguage(languageCode);

          // Obtener el nombre del idioma en formato legible
          const languageNames = {
            en: "English",
            es: "Español",
            fil: "Filipino",
          };

          console.log(
            `✅ Idioma cambiado exitosamente a: ${languageNames[languageCode]}`
          );

          return `Idioma cambiado exitosamente a ${languageNames[languageCode]}`;
        } catch (error) {
          console.error("❌ Error en client tool change_language:", error);
          return `Error al cambiar el idioma: ${error.message}`;
        }
      },
    },
  });

  // Scroll automático cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, conversation.isSpeaking]);

  // Limpiar la sesión cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (conversationStarted) {
        conversation.endSession();
      }
    };
  }, [conversationStarted]);

  const handleToggleMic = () => {
    setMicMuted((prev) => !prev);
  };

  const handleStartSession = async () => {
    if (conversationStarted) return;

    try {
      // 🔇 Mutear el micrófono para que el agente espere
      setMicMuted(true);
      console.log("🔇 Micrófono muteado durante la configuración inicial");

      // Obtener el token de Auth0
      const token = await getAccessTokenSilently();

      // Obtener la zona horaria del dispositivo
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log("🌍 Zona horaria detectada:", timezone);

      // Iniciar la sesión de ElevenLabs para obtener el conversation_id
      const sessionData = await conversation.startSession({
        agentId: "agent_1701k9redecrepv9gdngmgvep67q",
        dynamicVariables: {
          id_user: userInfo?.uid || user?.sub || "",
          first_name: userInfo?.user_info?.first_name || user?.given_name || "",
          last_name: userInfo?.user_info?.last_name || user?.family_name || "",
          complete_name: userInfo?.user_info?.complete_name || user?.name || "",
          email: userInfo?.user_info?.email_address || user?.email || "",
          company_name: userInfo?.user_info?.company_name || "",
          web_alias: userInfo?.user_info?.web_alias || "",
          country: userInfo?.user_info?.country || "",
          city: userInfo?.user_info?.city || "",
          language: userInfo?.user_info?.language || "en",
          token: token || "",
          timezone: timezone || "UTC",
        },
      });

      // El sessionData es directamente el conversation_id
      const conversationId = sessionData || "";
      console.log("📝 Conversation ID obtenido:", conversationId);
      console.log("📝 Tipo del Conversation ID:", typeof conversationId);

      // Enviar la información al endpoint store-session
      if (conversationId) {
        try {
          console.log("⏳ Enviando sesión al servidor...");
          const storeSessionResponse = await fetch(
            "https://wlshop.devserverwave4.com/whereby-mcp/api/store-session",
            {
              method: "POST",
              headers: {
                accept: "application/json",
                "X-API-Key": "sUzLNSv_XIZMSN3VbX9E_PO3ISyAdFtRxlRPCNlgRRc",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                conversation_id: conversationId,
                auth0_token: token,
                id_user: userInfo?.uid || user?.sub || "",
              }),
            }
          );

          if (storeSessionResponse.ok) {
            const responseData = await storeSessionResponse.json();
            console.log("✅ Sesión almacenada exitosamente:", responseData);
          } else {
            console.error(
              "❌ Error al almacenar la sesión:",
              storeSessionResponse.status,
              await storeSessionResponse.text()
            );
          }
        } catch (storeError) {
          console.error("❌ Error en la petición store-session:", storeError);
        }
      }

      // 🔊 Ahora sí, activar el micrófono para que el usuario pueda hablar
      setMicMuted(false);
      console.log(
        "🔊 Micrófono activado - El agente está listo para conversar"
      );
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      // En caso de error, también activar el micrófono
      setMicMuted(false);
    }
  };

  const handleEndSession = async () => {
    if (!conversationStarted) return;

    try {
      await conversation.endSession();
      setMessages([]);
    } catch (error) {
      console.error("Error al finalizar sesión:", error);
    }
  };

  const isConnecting = conversation.status === "connecting";
  const isConnected = conversation.status === "connected";

  return (
    <>
      {/* Botón flotante para abrir el chat */}
      <button
        className={`chat-widget-button ${isOpen ? "hidden" : ""}`}
        onClick={() => setIsOpen(true)}
        aria-label={t("title")}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
            fill="white"
          />
        </svg>
      </button>

      {/* Ventana del chat */}
      <div className={`chat-widget-container ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="chat-widget-header">
          <div className="chat-widget-header-content">
            <div className="chat-widget-avatar">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="chat-widget-header-text">
              <h3>{t("title")}</h3>
              <span
                className={`status-indicator ${
                  isConnected ? "connected" : "disconnected"
                }`}
              >
                {isConnecting
                  ? t("connecting")
                  : isConnected
                  ? t("status.connected")
                  : t("status.offline")}
              </span>
            </div>
          </div>
          <button
            className="chat-widget-close"
            onClick={() => setIsOpen(false)}
            aria-label={t("controls.endCall")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        {/* Messages area */}
        <div className="chat-widget-messages">
          {!conversationStarted && !isConnecting && (
            <div className="chat-widget-start">
              <div className="start-icon">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#F2B84B" opacity="0.2" />
                  <path
                    d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                    fill="#F2B84B"
                  />
                  <path
                    d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.92V21H13V17.92C16.39 17.43 19 14.53 19 11H17Z"
                    fill="#F2B84B"
                  />
                </svg>
              </div>
              <h3>{t("startScreen.title")}</h3>
              <p>{t("startScreen.description")}</p>
              <button
                className="start-session-button"
                onClick={handleStartSession}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.01 15.38C18.78 15.38 17.59 15.18 16.48 14.82C16.13 14.7 15.74 14.79 15.47 15.06L13.9 17.03C11.07 15.68 8.42 13.13 7.01 10.2L8.96 8.54C9.23 8.26 9.31 7.87 9.2 7.52C8.83 6.41 8.64 5.22 8.64 3.99C8.64 3.45 8.19 3 7.65 3H4.19C3.65 3 3 3.24 3 3.99C3 13.28 10.73 21 20.01 21C20.72 21 21 20.37 21 19.82V16.37C21 15.83 20.55 15.38 20.01 15.38Z"
                    fill="currentColor"
                  />
                </svg>
                {t("startScreen.startButton")}
              </button>
            </div>
          )}
          {isConnecting && (
            <div className="chat-widget-connecting">
              <div className="spinner"></div>
              <p>{t("connecting")}</p>
            </div>
          )}
          {messages.length === 0 && isConnected && (
            <div className="chat-widget-welcome">
              <p>{t("welcome")}</p>
            </div>
          )}
          {messages.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.role}`}>
              <div className="message-bubble">{msg.content}</div>
            </div>
          ))}
          {conversation.isSpeaking && (
            <div className="chat-message agent">
              <div className="message-bubble typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Controls - Solo botones de llamada */}
        {isConnected && (
          <div className="chat-widget-controls">
            <button
              className={`control-button mic-button ${
                micMuted ? "muted" : "active"
              }`}
              onClick={handleToggleMic}
              disabled={!isConnected}
              title={micMuted ? t("controls.unmute") : t("controls.mute")}
            >
              {micMuted ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 11H17.3C17 11 17 11.3 17 11.5V12C17 14.21 15.21 16 13 16H12.45L14.21 17.76C14.91 17.5 15.58 17.13 16.16 16.67L21.59 22.1L23 20.69L3.41 1.1L2 2.51L10.48 11H10.5V12C10.5 13.19 9.69 14 8.5 14H7.5C6.31 14 5.5 13.19 5.5 12V11H3V11C3 14.87 6.13 18 10 18V22H14V18C14 18 14 18 14 18L18.97 22.97L20.38 21.56L16.16 17.34C18.05 16.06 19 14.17 19 12V11Z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                    fill="currentColor"
                  />
                  <path
                    d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.92V21H13V17.92C16.39 17.43 19 14.53 19 11H17Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
            <button
              className="control-button end-button"
              onClick={handleEndSession}
              title={t("controls.endCall")}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.23 15.26L16.69 14.97C16.08 14.9 15.48 15.11 15.05 15.54L13.21 17.38C10.38 15.94 8.06 13.63 6.62 10.79L8.47 8.94C8.9 8.51 9.11 7.91 9.04 7.3L8.75 4.78C8.63 3.77 7.78 3.01 6.76 3.01H5.03C3.9 3.01 2.96 3.95 3.03 5.08C3.56 13.62 10.39 20.44 18.92 20.97C20.05 21.04 20.99 20.1 20.99 18.97V17.24C21 16.23 20.24 15.38 19.23 15.26Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ElevenLabsSDKWidget;
